import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DateRangeFilter } from '../components/ui/DateRangeFilter';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Building2, Banknote, Tags, Palette, Truck } from 'lucide-react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import useConfig from '../hooks/useConfig';
import { formatCurrency, getFluxInfo } from '../utils/formatters';

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filters, setFilters] = useState({ type_flux: '', category_id: '', supplier_id: '' });
  const [summaryByFlux, setSummaryByFlux] = useState({});
  const [activeTab, setActiveTab] = useState('expenses');
  
  // Load flux types from config
  const { options: configOptions, loading: configLoading } = useConfig(['FLUX_TYPE']);
  const fluxTypes = configOptions.FLUX_TYPE || [];
  
  // Get default flux code (first one or CASH_CAISSE)
  const defaultFluxCode = fluxTypes.find(f => f.metadata?.is_cash === true)?.code 
    || fluxTypes[0]?.code 
    || 'CASH_CAISSE';
  
  // Date filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [formData, setFormData] = useState({
    category_id: '',
    supplier_id: '',
    description: '',
    amount: '',
    expense_date: format(new Date(), 'yyyy-MM-dd'),
    type_flux: defaultFluxCode,
    supplier: '', // Legacy field
    notes: ''
  });

  // Update formData.type_flux when config loads
  useEffect(() => {
    if (!configLoading && fluxTypes.length > 0 && !formData.type_flux) {
      setFormData(prev => ({ ...prev, type_flux: defaultFluxCode }));
    }
  }, [configLoading, fluxTypes, defaultFluxCode]);

  const [categoryForm, setCategoryForm] = useState({
    name: '',
    color: '#6B7280'
  });

  const colorOptions = [
    { value: '#EF4444', label: 'Rouge' },
    { value: '#F97316', label: 'Orange' },
    { value: '#EAB308', label: 'Jaune' },
    { value: '#22C55E', label: 'Vert' },
    { value: '#06B6D4', label: 'Cyan' },
    { value: '#3B82F6', label: 'Bleu' },
    { value: '#8B5CF6', label: 'Violet' },
    { value: '#EC4899', label: 'Rose' },
    { value: '#6B7280', label: 'Gris' }
  ];

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
    fetchSuppliers();
  }, [filters, dateFrom, dateTo]);

  const fetchExpenses = async () => {
    try {
      const params = { ...filters, limit: 100 };
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const response = await api.get('/expenses', { params });
      setExpenses(response.data.data);
      
      // Calculate summary dynamically by flux
      const byFlux = {};
      response.data.data.forEach(e => {
        const flux = e.type_flux || 'OTHER';
        if (!byFlux[flux]) byFlux[flux] = 0;
        byFlux[flux] += parseFloat(e.amount);
      });
      setSummaryByFlux(byFlux);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories', { params: { type: 'EXPENSE' } });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/suppliers', { params: { active: true } });
      setSuppliers(response.data.data || response.data || []);
    } catch (error) {
      console.error('Failed to fetch suppliers');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedExpense) {
        await api.put(`/expenses/${selectedExpense.id}`, formData);
        toast.success('Dépense mise à jour');
      } else {
        await api.post('/expenses', formData);
        toast.success('Dépense créée');
      }
      setShowDialog(false);
      resetForm();
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success('Dépense supprimée');
      fetchExpenses();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      category_id: '',
      supplier_id: '',
      description: '',
      amount: '',
      expense_date: format(new Date(), 'yyyy-MM-dd'),
      type_flux: defaultFluxCode,
      supplier: '',
      notes: ''
    });
    setSelectedExpense(null);
  };

  const openEditDialog = (expense) => {
    setSelectedExpense(expense);
    setFormData({
      category_id: expense.category_id ? String(expense.category_id) : '',
      supplier_id: expense.supplier_id ? String(expense.supplier_id) : '',
      description: expense.description || '',
      amount: expense.amount || '',
      expense_date: expense.expense_date || format(new Date(), 'yyyy-MM-dd'),
      type_flux: expense.type_flux || defaultFluxCode,
      supplier: expense.supplier || '',
      notes: expense.notes || ''
    });
    setShowDialog(true);
  };

  // Category CRUD
  const resetCategoryForm = () => {
    setCategoryForm({ name: '', color: '#6B7280' });
    setSelectedCategory(null);
  };

  const openEditCategoryDialog = (category) => {
    setSelectedCategory(category);
    setCategoryForm({
      name: category.name,
      color: category.color || '#6B7280'
    });
    setShowCategoryDialog(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryForm.name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    try {
      if (selectedCategory) {
        await api.put(`/categories/${selectedCategory.id}`, { ...categoryForm, type: 'EXPENSE' });
        toast.success('Catégorie mise à jour');
      } else {
        await api.post('/categories', { ...categoryForm, type: 'EXPENSE' });
        toast.success('Catégorie créée');
      }
      setShowCategoryDialog(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ? Elle sera désactivée.')) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success('Catégorie désactivée');
      fetchCategories();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Dépenses</h1>
          <p className="text-muted-foreground mt-1">Suivi des dépenses</p>
        </div>
        {activeTab === 'expenses' && (
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle dépense
          </Button>
        )}
        {activeTab === 'categories' && (
          <Button onClick={() => { resetCategoryForm(); setShowCategoryDialog(true); }} data-testid="add-category-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle catégorie
          </Button>
        )}
      </div>

      {/* Date Filter */}
      <Card className="p-4">
        <DateRangeFilter
          startDate={dateFrom}
          endDate={dateTo}
          onStartDateChange={setDateFrom}
          onEndDateChange={setDateTo}
          onReset={() => { setDateFrom(''); setDateTo(''); }}
          showQuickFilters={true}
        />
      </Card>

      {/* Summary Cards - Dynamic by flux */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {fluxTypes.map(flux => {
          const fluxInfo = getFluxInfo(fluxTypes, flux.code);
          const total = summaryByFlux[flux.code] || 0;
          return (
            <Card key={flux.code} className="card-metric border-l-4" style={{ borderLeftColor: fluxInfo.color }}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${fluxInfo.color}20` }}>
                    {flux.code === 'SOCIETE' ? <Building2 className="w-5 h-5" style={{ color: fluxInfo.color }} /> : <Banknote className="w-5 h-5" style={{ color: fluxInfo.color }} />}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{fluxInfo.label}</p>
                    <p className="text-2xl font-bold" style={{ color: fluxInfo.color }}>{formatCurrency(total)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        <Card className="card-metric">
          <CardContent className="p-4">
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{formatCurrency(Object.values(summaryByFlux).reduce((a, b) => a + b, 0))}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="expenses" data-testid="tab-expenses">
            <Banknote className="w-4 h-4 mr-2" />
            Dépenses
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">
            <Tags className="w-4 h-4 mr-2" />
            Catégories
          </TabsTrigger>
        </TabsList>

        {/* Expenses Tab */}
        <TabsContent value="expenses" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <Select value={filters.type_flux || 'all'} onValueChange={(v) => setFilters({ ...filters, type_flux: v === 'all' ? '' : v })}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Type de flux" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les flux</SelectItem>
                    {fluxTypes.map(flux => (
                      <SelectItem key={flux.code} value={flux.code}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: flux.color || flux.metadata?.color }} />
                          {flux.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.category_id || 'all'} onValueChange={(v) => setFilters({ ...filters, category_id: v === 'all' ? '' : v })}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={String(cat.id)}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Expenses List */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
              ) : expenses.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  Aucune dépense trouvée
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Description</th>
                        <th>Catégorie</th>
                        <th>Flux</th>
                        <th>Montant</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map((expense) => (
                        <tr key={expense.id} data-testid={`expense-row-${expense.id}`}>
                          <td>{format(new Date(expense.expense_date), 'dd/MM/yyyy')}</td>
                          <td>
                            <div>
                              <p className="font-medium">{expense.description}</p>
                              {expense.supplierRef ? (
                                <Link 
                                  to={`/suppliers?id=${expense.supplierRef.id}`}
                                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                >
                                  <Truck className="w-3 h-3" />
                                  {expense.supplierRef.name}
                                </Link>
                              ) : expense.supplier && (
                                <p className="text-xs text-muted-foreground">{expense.supplier}</p>
                              )}
                            </div>
                          </td>
                          <td>
                            {expense.category && (
                              <Badge variant="outline" style={{ borderColor: expense.category.color, color: expense.category.color }}>
                                {expense.category.name}
                              </Badge>
                            )}
                          </td>
                          <td>
                            {(() => {
                              const fluxInfo = getFluxInfo(fluxTypes, expense.type_flux);
                              return (
                                <Badge style={{ backgroundColor: `${fluxInfo.color}20`, color: fluxInfo.color, borderColor: fluxInfo.color }}>
                                  {fluxInfo.label}
                                </Badge>
                              );
                            })()}
                          </td>
                          <td className="font-semibold text-red-600">
                            -{formatCurrency(expense.amount)}
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(expense)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDelete(expense.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tags className="w-5 h-5" />
                Catégories de dépenses
              </CardTitle>
            </CardHeader>
            <CardContent>
              {categories.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Tags className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune catégorie de dépenses</p>
                  <p className="text-sm">Créez votre première catégorie pour organiser vos dépenses</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map((category) => (
                    <div
                      key={category.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                      data-testid={`category-card-${category.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: category.color || '#6B7280' }}
                        />
                        <span className="font-medium">{category.name}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => openEditCategoryDialog(category)}
                          data-testid={`edit-category-${category.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDeleteCategory(category.id)}
                          data-testid={`delete-category-${category.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Expense Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedExpense ? 'Modifier la dépense' : 'Nouvelle dépense'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Montant *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Type de flux *</Label>
                <Select value={formData.type_flux} onValueChange={(v) => setFormData({ ...formData, type_flux: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {fluxTypes.map(flux => (
                      <SelectItem key={flux.code} value={flux.code}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: flux.color || flux.metadata?.color }} />
                          {flux.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={String(cat.id)}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Fournisseur (optionnel)</Label>
              <Select 
                value={formData.supplier_id ? String(formData.supplier_id) : 'none'} 
                onValueChange={(v) => setFormData({ ...formData, supplier_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un fournisseur..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun fournisseur</SelectItem>
                  {suppliers.map((sup) => (
                    <SelectItem key={sup.id} value={String(sup.id)}>{sup.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Ou saisie libre : <Input
                  className="mt-1"
                  placeholder="Nom du fournisseur..."
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value, supplier_id: '' })}
                />
              </p>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {selectedExpense ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div>
              <Label>Nom de la catégorie *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="Ex: Courses, Transport, Réparations..."
                required
                data-testid="category-name-input"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Couleur
              </Label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setCategoryForm({ ...categoryForm, color: color.value })}
                    className={`w-10 h-10 rounded-lg border-2 transition-all ${
                      categoryForm.color === color.value 
                        ? 'border-primary scale-110' 
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                    data-testid={`color-${color.value}`}
                  />
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCategoryDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" data-testid="save-category-btn">
                {selectedCategory ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
